import { useMemo } from "react";
import dayjs from "dayjs";

// Components
// Hooks
import useTranslate from "hooks/useTranslate";

// Icons
import ClockIcon from "assets/icons/glory/clock-forward.svg?react";
import BonusAvaliableQueueIcon from "assets/icons/glory/bonus_avaliable_queue.svg?react";

// Images
import MoneyBonus from "assets/images/glory/image_bonus.webp";
import BonusQueue from "assets/images/glory/bonus_queue.webp";

// Types
import {
  BONUS_ITEM,
  BONUS_STATUS,
  BONUS_TYPES,
  BonusListType,
  FREE_SPINS_STAGES,
} from "types/Bonus";
import useFormatNumber from "hooks/useFormatNumber";
import BonusItem from "../../atoms/BonusItem";
import Timer from "../../atoms/Timer";

import "./styles.sass";

type BonusCardInfoProps = {
  bonus: BonusListType;
  currency: string;
};

const BonusCardInfo = (props: BonusCardInfoProps) => {
  const { bonus, currency } = props;
  const t = useTranslate();
  const formatNumber = useFormatNumber();
  const isFreeSpins =
    "freeSpinsGame" in bonus && bonus.type === BONUS_TYPES.FREE_SPINS;

  let image = MoneyBonus;
  if ("game" in bonus && bonus.game) {
    const cleanedUrl = bonus.game.urlAlias.replace("mobile-", "");
    image = `https://diswdgcu9cfva.cloudfront.net/covers2/${cleanedUrl}.webp`;
  }
  if (
    bonus.type === BONUS_TYPES.NOMINAL ||
    bonus.type === BONUS_TYPES.TRIGGER
  ) {
    image = BonusQueue;
  }
  if (bonus.type === BONUS_TYPES.FREE_SPINS && "gameUrlAlias" in bonus) {
    image = `https://diswdgcu9cfva.cloudfront.net/covers2/${bonus.gameUrlAlias}.webp`;
  }

  const dateStart =
    "startDate" in bonus ? dayjs(bonus.startDate).format("DD.MM.YYYY") : "";

  const game = "game" in bonus ? bonus.game : null;
  const expiredDate = useMemo(() => {
    if ("wagerExpireAt" in bonus && bonus.wagerExpireAt) {
      return bonus.wagerExpireAt;
    }
    if ("freeSpinsExpireAt" in bonus && bonus.freeSpinsExpireAt) {
      return bonus.freeSpinsExpireAt;
    }
    if ("expireDate" in bonus && bonus.expireDate) {
      return bonus.expireDate;
    }
    if ("expireAt" in bonus && bonus.expireAt) {
      return bonus.expireAt;
    }
    return "";
  }, [bonus]);

  const bonusTitle = useMemo(() => {
    if (isFreeSpins && bonus.freeSpinStage === FREE_SPINS_STAGES.AVAILABLE) {
      return bonus.textBeforeReceivingAward;
    }
    if (isFreeSpins && bonus.freeSpinStage === FREE_SPINS_STAGES.QUEUE) {
      return bonus.textAfterReceivingAward;
    }
    if (isFreeSpins && bonus.freeSpinStage === FREE_SPINS_STAGES.UNUSED) {
      return bonus.nameGameFreeSpin;
    }
    if (isFreeSpins && bonus.freeSpinStage === FREE_SPINS_STAGES.USED) {
      return t("bonus.card.front.title.freeSpins.wager");
    }
    if (bonus.type === BONUS_TYPES.SPINS) {
      return t("bonus.block.spins");
    }
    if (bonus.type === BONUS_TYPES.REWARD) {
      return t("bonus.block.money");
    }
    if (
      bonus.type === BONUS_TYPES.NOMINAL ||
      bonus.type === BONUS_TYPES.TRIGGER
    ) {
      if (
        bonus.status.toLowerCase() === BONUS_STATUS.ACTIVE &&
        "textAfterReceivingAward" in bonus
      ) {
        return bonus.textAfterReceivingAward;
      }
      if (
        (bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE ||
          bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE_QUEUE ||
          bonus.status.toLowerCase() === BONUS_STATUS.QUEUE) &&
        "textBeforeReceivingAward" in bonus
      ) {
        return (
          <div className="BonusCardInfo__title">
            {bonus.textBeforeReceivingAward}{" "}
            <span>{formatNumber(bonus.amount / 100, 0, true)}</span>
          </div>
        );
      }
    }
    return "";
  }, [bonus, t, formatNumber, isFreeSpins]);

  const bonusName = useMemo(() => {
    if (bonus.type === BONUS_TYPES.SPINS) {
      return "bonus.block.attribute.spins";
    }
    if (isFreeSpins && bonus.freeSpinStage === FREE_SPINS_STAGES.UNUSED) {
      return "bonus.block.attribute.spins";
    }
    if (isFreeSpins && bonus.freeSpinStage !== FREE_SPINS_STAGES.UNUSED) {
      return "";
    }
    if (bonus.type === BONUS_TYPES.REWARD) {
      return "bonus.block.attribute.reward";
    }
    if (
      bonus.status.toLowerCase() === BONUS_STATUS.ACTIVE ||
      bonus.status.toLowerCase() === BONUS_STATUS.QUEUE
    ) {
      if (
        bonus.type === BONUS_TYPES.TRIGGER ||
        bonus.type === BONUS_TYPES.NOMINAL
      ) {
        return "bonus.wagering.period";
      }
      return "profile.balance.header.available";
    }
    if (
      bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE ||
      bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE_QUEUE
    ) {
      return "bonus.queue.period";
    }
    return "";
  }, [bonus, isFreeSpins]);

  const bonusType = useMemo(() => {
    if (bonus.type === BONUS_TYPES.FREE_SPINS) {
      return BONUS_ITEM.FREE_SPINS;
    }
    if (bonus.type === BONUS_TYPES.SPINS) {
      return BONUS_ITEM.TOTAL_SPINS;
    }
    if (bonus.type === BONUS_TYPES.REWARD) {
      return BONUS_ITEM.REWARD;
    }
    if (bonus.type === BONUS_TYPES.NOMINAL) {
      if (bonus.status === BONUS_STATUS.ACTIVE) {
        return BONUS_ITEM.NOMINAL;
      }
      if (bonus.status.toLowerCase() === BONUS_STATUS.QUEUE) {
        return BONUS_ITEM.TIMER;
      }
    }
    if (bonus.type === BONUS_TYPES.TRIGGER) {
      if (bonus.status.toLowerCase() === BONUS_STATUS.ACTIVE) {
        return BONUS_ITEM.TRIGGER;
      }
      if (bonus.status.toLowerCase() === BONUS_STATUS.QUEUE) {
        return BONUS_ITEM.TIMER;
      }
    }
    return BONUS_ITEM.TIMER;
  }, [bonus]);

  const bonusNominalTriggerVal =
    "amount" in bonus && (bonus.amount || bonus.amount === 0)
      ? formatNumber(bonus.amount / 100, 0, true)
      : "";

  return (
    <div className="BonusCardInfo">
      {bonus.status.toLowerCase() === BONUS_STATUS.DEACTIVE && (
        <div className="BonusCardInfo-deactive">
          <ClockIcon className="BonusCardInfo-deactive__clock" />
          <span className="BonusCardInfo-deactive__date">{dateStart}</span>
        </div>
      )}
      {bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE_QUEUE && (
        <div className="BonusCardInfo-deactive">
          <BonusAvaliableQueueIcon className="BonusCardInfo-deactive__clock" />
          <span className="BonusCardInfo-deactive__text">
            {t("bonus.trigger.avaliable.queue")}
          </span>
        </div>
      )}
      <img
        className="BonusCardInfo__img"
        src={image}
        alt={game ? game.name : ""}
      />
      <div className="BonusCardInfo__info">
        <div className="BonusCardInfo__name">
          {bonusTitle}{" "}
          {(bonus.type === BONUS_TYPES.NOMINAL ||
            bonus.type === BONUS_TYPES.TRIGGER) &&
            bonus.status === BONUS_STATUS.ACTIVE && (
              <span className="BonusCardInfo__name_val">
                {bonusNominalTriggerVal}
              </span>
            )}
        </div>
        <BonusItem
          name={bonusName}
          type={bonusType}
          bonus={bonus}
          currency={currency}
        />
        <div className="BonusCardInfo__timer">
          {(bonus.status.toLowerCase() === BONUS_STATUS.ACTIVE ||
            bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE) && (
            <Timer finishDate={expiredDate} isBonus />
          )}
          {(bonus.status.toLowerCase() === BONUS_STATUS.QUEUE ||
            bonus.status.toLowerCase() === BONUS_STATUS.AVAILABLE_QUEUE) && (
            <Timer
              finishDate={expiredDate}
              defaultValue="48:00:00"
              isBonus
              disabled
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BonusCardInfo;
